import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Baby, Plus, Edit2, Trash2, Save, X, User, Heart, 
  AlertTriangle, Pill, Phone, Stethoscope, Dna, Apple,
  Cloud, Brain, ChevronDown, ChevronUp, Shield
} from 'lucide-react';
import { formatLocalDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Collapsible Section Component
const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition-colors"
      >
        <div className="flex items-center gap-2 font-medium text-[#2D2D2D]">
          <Icon className="w-5 h-5 text-primary" />
          {title}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {isOpen && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
};

// Tag Input Component
const TagInput = ({ label, tags, setTags, placeholder }) => {
  const [input, setInput] = useState('');
  
  const addTag = () => {
    if (input.trim() && !tags.includes(input.trim())) {
      setTags([...tags, input.trim()]);
      setInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  return (
    <div>
      <label className="text-sm font-medium text-[#2D2D2D] block mb-1">{label}</label>
      <div className="flex gap-2 mb-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button type="button" onClick={addTag} variant="outline" size="sm">Add</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, i) => (
          <span key={i} className="bg-primary/10 text-primary px-2 py-1 rounded-full text-sm flex items-center gap-1">
            {tag}
            <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

// Profile Form Component
const ProfileForm = ({ profile, onSave, onCancel, isNew }) => {
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    date_of_birth: profile?.date_of_birth || '',
    blood_type: profile?.blood_type || '',
    weight: profile?.weight || '',
    allergies: profile?.allergies || [],
    current_diagnoses: profile?.current_diagnoses || [],
    mthfr_status: profile?.mthfr_status || '',
    food_triggers: profile?.food_triggers || [],
    environmental_triggers: profile?.environmental_triggers || [],
    stress_triggers: profile?.stress_triggers || [],
    safe_medications: profile?.safe_medications || [],
    medications_to_avoid: profile?.medications_to_avoid || [],
    current_medications: profile?.current_medications || [],
    current_supplements: profile?.current_supplements || [],
    notes: profile?.notes || '',
    insurance_info: profile?.insurance_info || '',
  });

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Basic Info - Always Open */}
      <Section title="Basic Information" icon={User} defaultOpen={true}>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-[#2D2D2D] block mb-1">Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Child's name"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2D2D2D] block mb-1">Date of Birth</label>
            <Input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => updateField('date_of_birth', e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2D2D2D] block mb-1">Blood Type</label>
            <select
              value={formData.blood_type}
              onChange={(e) => updateField('blood_type', e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-stone-200 bg-white"
            >
              <option value="">Select...</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
              <option value="Unknown">Unknown</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#2D2D2D] block mb-1">Weight</label>
            <Input
              value={formData.weight}
              onChange={(e) => updateField('weight', e.target.value)}
              placeholder="e.g., 45 lbs"
            />
          </div>
        </div>
        <TagInput
          label="Allergies"
          tags={formData.allergies}
          setTags={(tags) => updateField('allergies', tags)}
          placeholder="Add allergy..."
        />
      </Section>

      {/* Diagnoses */}
      <Section title="Diagnoses" icon={Heart}>
        <TagInput
          label="Current Diagnoses"
          tags={formData.current_diagnoses}
          setTags={(tags) => updateField('current_diagnoses', tags)}
          placeholder="e.g., PANDAS, PANS, MCAS..."
        />
      </Section>

      {/* Genetics */}
      <Section title="Genetic Information" icon={Dna}>
        <div>
          <label className="text-sm font-medium text-[#2D2D2D] block mb-1">MTHFR Status</label>
          <select
            value={formData.mthfr_status}
            onChange={(e) => updateField('mthfr_status', e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-stone-200 bg-white"
          >
            <option value="">Select...</option>
            <option value="Normal">Normal (No variants)</option>
            <option value="C677T Heterozygous">C677T Heterozygous (+/-)</option>
            <option value="C677T Homozygous">C677T Homozygous (+/+)</option>
            <option value="A1298C Heterozygous">A1298C Heterozygous (+/-)</option>
            <option value="A1298C Homozygous">A1298C Homozygous (+/+)</option>
            <option value="Compound Heterozygous">Compound Heterozygous (C677T +/- & A1298C +/-)</option>
            <option value="Not Tested">Not Tested</option>
          </select>
        </div>
      </Section>

      {/* Triggers */}
      <Section title="Known Triggers" icon={AlertTriangle}>
        <TagInput
          label="Food Triggers"
          tags={formData.food_triggers}
          setTags={(tags) => updateField('food_triggers', tags)}
          placeholder="e.g., gluten, dairy..."
        />
        <TagInput
          label="Environmental Triggers"
          tags={formData.environmental_triggers}
          setTags={(tags) => updateField('environmental_triggers', tags)}
          placeholder="e.g., mold, pollen..."
        />
        <TagInput
          label="Stress Triggers"
          tags={formData.stress_triggers}
          setTags={(tags) => updateField('stress_triggers', tags)}
          placeholder="e.g., school tests, transitions..."
        />
      </Section>

      {/* Medications */}
      <Section title="Medications & Supplements" icon={Pill}>
        <TagInput
          label="Current Medications"
          tags={formData.current_medications}
          setTags={(tags) => updateField('current_medications', tags)}
          placeholder="Add current medication..."
        />
        <TagInput
          label="Current Supplements"
          tags={formData.current_supplements}
          setTags={(tags) => updateField('current_supplements', tags)}
          placeholder="Add supplement..."
        />
        <TagInput
          label="Safe Medications"
          tags={formData.safe_medications}
          setTags={(tags) => updateField('safe_medications', tags)}
          placeholder="Medications that work well..."
        />
        <TagInput
          label="Medications to Avoid"
          tags={formData.medications_to_avoid}
          setTags={(tags) => updateField('medications_to_avoid', tags)}
          placeholder="Medications to avoid..."
        />
      </Section>

      {/* Additional Notes */}
      <Section title="Additional Information" icon={Shield}>
        <div>
          <label className="text-sm font-medium text-[#2D2D2D] block mb-1">Insurance Information</label>
          <Input
            value={formData.insurance_info}
            onChange={(e) => updateField('insurance_info', e.target.value)}
            placeholder="Insurance provider, policy number..."
          />
        </div>
        <div>
          <label className="text-sm font-medium text-[#2D2D2D] block mb-1">Notes</label>
          <Textarea
            value={formData.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            placeholder="Any additional notes about your child..."
            rows={3}
          />
        </div>
      </Section>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="submit" disabled={saving} className="bg-primary text-white">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : (isNew ? 'Create Profile' : 'Save Changes')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Profile Card Component
const ProfileCard = ({ profile, onEdit, onDelete }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <Card className="p-6 border border-stone-200">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Baby className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="text-xl font-nunito font-bold text-[#2D2D2D]">{profile.name}</h3>
            {profile.date_of_birth && (
              <p className="text-sm text-[#57534E]">
                Born: {formatLocalDate(profile.date_of_birth)}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(profile)}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-500 hover:text-red-600 hover:border-red-300"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Quick Info Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {profile.blood_type && (
          <div className="text-center p-2 bg-stone-50 rounded-lg">
            <p className="text-xs text-[#57534E]">Blood Type</p>
            <p className="font-semibold text-[#2D2D2D]">{profile.blood_type}</p>
          </div>
        )}
        {profile.weight && (
          <div className="text-center p-2 bg-stone-50 rounded-lg">
            <p className="text-xs text-[#57534E]">Weight</p>
            <p className="font-semibold text-[#2D2D2D]">{profile.weight}</p>
          </div>
        )}
        {profile.mthfr_status && (
          <div className="text-center p-2 bg-stone-50 rounded-lg">
            <p className="text-xs text-[#57534E]">MTHFR</p>
            <p className="font-semibold text-[#2D2D2D] text-xs">{profile.mthfr_status}</p>
          </div>
        )}
      </div>

      {/* Diagnoses */}
      {profile.current_diagnoses?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-[#57534E] mb-1">Diagnoses</p>
          <div className="flex flex-wrap gap-1">
            {profile.current_diagnoses.map((d, i) => (
              <span key={i} className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs">
                {d}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Allergies */}
      {profile.allergies?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-[#57534E] mb-1">Allergies</p>
          <div className="flex flex-wrap gap-1">
            {profile.allergies.map((a, i) => (
              <span key={i} className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs">
                {a}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700 mb-3">
            Are you sure you want to delete {profile.name}'s profile? This cannot be undone.
          </p>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              onClick={() => { onDelete(profile.profile_id); setShowDeleteConfirm(false); }}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

// Main Page Component
export const ChildProfilesPage = () => {
  const { token } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileData, setProfileData] = useState({ max_allowed: 1, can_add_more: true, tier: 1 });

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/child-profiles/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProfiles(data.profiles || []);
        setProfileData({
          max_allowed: data.max_allowed,
          can_add_more: data.can_add_more,
          tier: data.tier
        });
      }
    } catch (err) {
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      const url = editingProfile 
        ? `${API_URL}/api/child-profiles/${editingProfile.profile_id}`
        : `${API_URL}/api/child-profiles/`;
      
      const res = await fetch(url, {
        method: editingProfile ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingProfile ? 'Profile updated!' : 'Profile created!');
        setShowForm(false);
        setEditingProfile(null);
        fetchProfiles();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save profile');
      }
    } catch (err) {
      toast.error('Failed to save profile');
    }
  };

  const handleDelete = async (profileId) => {
    try {
      const res = await fetch(`${API_URL}/api/child-profiles/${profileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Profile deleted');
        fetchProfiles();
      } else {
        toast.error('Failed to delete profile');
      }
    } catch (err) {
      toast.error('Failed to delete profile');
    }
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProfile(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="child-profiles-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-6 md:p-8">
        <div className="flex items-center gap-4 mb-2">
          <Baby className="w-10 h-10 text-primary" strokeWidth={1.5} />
          <h1 className="text-2xl md:text-3xl font-nunito font-bold text-[#2D2D2D]">
            Child Profiles
          </h1>
        </div>
        <p className="text-[#57534E]">
          Manage your child's medical information, triggers, medications, and more in one place.
        </p>
        <div className="mt-4 flex items-center gap-4">
          <span className="text-sm text-[#57534E]">
            {profiles.length} of {profileData.max_allowed} profile{profileData.max_allowed !== 1 ? 's' : ''} used
          </span>
          {profileData.tier === 1 && profiles.length >= 1 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
              Upgrade to Premium for multiple children
            </span>
          )}
        </div>
      </div>

      {/* Add Button */}
      {!showForm && profileData.can_add_more && (
        <Button 
          onClick={() => setShowForm(true)}
          className="bg-primary text-white"
          data-testid="add-profile-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Child Profile
        </Button>
      )}

      {/* Form */}
      {showForm && (
        <Card className="p-6 border border-stone-200">
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4">
            {editingProfile ? `Edit ${editingProfile.name}'s Profile` : 'New Child Profile'}
          </h2>
          <ProfileForm
            profile={editingProfile}
            onSave={handleSave}
            onCancel={handleCancel}
            isNew={!editingProfile}
          />
        </Card>
      )}

      {/* Profiles List */}
      {!showForm && (
        <div className="space-y-4">
          {profiles.length === 0 ? (
            <Card className="p-8 text-center border border-stone-200">
              <Baby className="w-16 h-16 text-primary/30 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#2D2D2D] mb-2">No profiles yet</h3>
              <p className="text-[#57534E] mb-4">
                Add your child's profile to keep track of their medical information.
              </p>
            </Card>
          ) : (
            profiles.map(profile => (
              <ProfileCard
                key={profile.profile_id}
                profile={profile}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ChildProfilesPage;
